import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getProperties } from "../api/properties.api";
import { isFeatureEnabled, usePlatformContext } from "./PlatformContext";
import { isUiFeatureEnabled } from "../utils/featureControl";
import { isUnauthorizedError } from "../api/http";
import { logActivity } from "../utils/activity";

const PropertiesContext = createContext(null);

let cachedProperties = null;
let cachedPagination = null;
let cachedError = null;
let inflightPromise = null;
let cachedAuthError = false;

const toArray = (items) => (Array.isArray(items) ? items : []);

export function PropertiesProvider({ children }) {
  const { features, isLoading: platformLoading } = usePlatformContext();
  const propertiesEnabled = isFeatureEnabled(features, "properties");
  const uiPropertiesEnabled = isUiFeatureEnabled("properties");
  const [properties, setProperties] = useState(() => toArray(cachedProperties));
  const [pagination, setPagination] = useState(() => cachedPagination);
  const [loading, setLoading] = useState(() => !cachedProperties);
  const [error, setError] = useState(() => cachedError);

  const fetchProperties = useCallback(async ({ force = false, page = 1, pageSize = 20 } = {}) => {
    if (!propertiesEnabled || !uiPropertiesEnabled || platformLoading) {
      setLoading(false);
      setProperties([]);
      setPagination(null);
      setError(null);
      return { items: [], pagination: null };
    }
    if (cachedAuthError) {
      setLoading(false);
      setError(cachedError || new Error("AUTH_REQUIRED"));
      return Promise.reject(cachedError || new Error("AUTH_REQUIRED"));
    }
    if (cachedProperties && !force) {
      setLoading(false);
      return { items: cachedProperties, pagination: cachedPagination };
    }
    if (inflightPromise && !force) {
      return inflightPromise;
    }
    setLoading(true);
    inflightPromise = getProperties(page, pageSize)
      .then((result) => {
        const items = toArray(result?.items);
        const nextPagination = result
          ? {
              page: result.page ?? null,
              pageSize: result.page_size ?? null,
              total: result.total ?? null,
            }
          : null;
        cachedProperties = items;
        cachedPagination = nextPagination;
        cachedError = null;
        setProperties(items);
        setPagination(nextPagination);
        setError(null);
        return { items, pagination: nextPagination };
      })
      .catch((err) => {
        if (isUnauthorizedError(err)) {
          cachedAuthError = true;
        }
        cachedError = err;
        setProperties([]);
        setPagination(null);
        setError(err);
        logActivity("api_error", { module: "properties" });
        throw err;
      })
      .finally(() => {
        inflightPromise = null;
        setLoading(false);
      });
    return inflightPromise;
  }, [platformLoading, propertiesEnabled, uiPropertiesEnabled]);

  const refreshProperties = useCallback(() => {
    if (!propertiesEnabled || !uiPropertiesEnabled || platformLoading) {
      setLoading(false);
      setProperties([]);
      setPagination(null);
      setError(null);
      return Promise.resolve({ items: [], pagination: null });
    }
    if (cachedAuthError) {
      return Promise.reject(cachedError || new Error("AUTH_REQUIRED"));
    }
    cachedProperties = null;
    cachedPagination = null;
    cachedError = null;
    return fetchProperties({ force: true });
  }, [fetchProperties, platformLoading, propertiesEnabled, uiPropertiesEnabled]);

  const upsertProperty = useCallback((nextProperty) => {
    if (!nextProperty?.id) return;
    const current = toArray(cachedProperties);
    const updated = current.some((property) => property.id === nextProperty.id)
      ? current.map((property) =>
          property.id === nextProperty.id ? { ...property, ...nextProperty } : property
        )
      : [nextProperty, ...current];
    cachedProperties = updated;
    setProperties(updated);
  }, []);

  const removeProperty = useCallback((id) => {
    if (!id) return;
    const updated = toArray(cachedProperties).filter((property) => property.id !== id);
    cachedProperties = updated;
    setProperties(updated);
  }, []);

  useEffect(() => {
    if (!propertiesEnabled || !uiPropertiesEnabled || platformLoading) {
      setLoading(false);
      setProperties([]);
      setPagination(null);
      setError(null);
      return;
    }
    fetchProperties().catch(() => {});
  }, [fetchProperties, platformLoading, propertiesEnabled, uiPropertiesEnabled]);

  const value = useMemo(
    () => ({
      properties,
      pagination,
      loading,
      error,
      refreshProperties,
      upsertProperty,
      removeProperty,
    }),
    [properties, pagination, loading, error, refreshProperties, upsertProperty, removeProperty]
  );

  return <PropertiesContext.Provider value={value}>{children}</PropertiesContext.Provider>;
}

export function useProperties() {
  const context = useContext(PropertiesContext);
  if (!context) {
    throw new Error("useProperties must be used within PropertiesProvider");
  }
  return context;
}

export const clearPropertiesCache = () => {
  cachedProperties = null;
  cachedPagination = null;
  cachedError = null;
  inflightPromise = null;
  cachedAuthError = false;
};
